/* eslint-disable no-restricted-globals */
import { useCallback, useEffect, useState } from "react";
import { IS_SERVER, ModalIds } from "@/constants";
const MODALS_TO_REMOVE_ON_REFRESH = [ModalIds.MULTI_INQUIRY];
// Read single "modal" from URL and return as a one item stack.
function readSingleFromUrl(loc) {
    const params = new URLSearchParams(loc.search);
    const one = params.get("modal");
    return one ? [one] : [];
}
// Write URL for a given stack: keep only a single ?modal=<top>.
function writeUrlForStack(loc, stack) {
    const u = new URL(loc.href);
    const params = u.searchParams;
    // Clear any previous modal-related keys.
    params.delete("modal");
    // Only show the top modal in the URL (or none if the stack is empty).
    const top = stack.at(-1);
    if (top)
        params.set("modal", top);
    return u.pathname + u.search + u.hash;
}
// Ensure modal is unique and placed on top.
function ensureOnTop(stack, id) {
    const next = stack.filter((x) => x !== id);
    next.push(id);
    return next;
}
// Remove a modal id from the stack.
function removeFromStack(stack, id) {
    return stack.filter((x) => x !== id);
}
// Check if modal is present in the stack.
function isInStack(stack, id) {
    return stack.includes(id);
}
// Read current navigation stack from history.state, otherwise derive from URL.
function readNavStack() {
    var _a;
    // Prefer history state when available.
    const state = ((_a = history.state) !== null && _a !== void 0 ? _a : {});
    if (Array.isArray(state.modalStack)) {
        // Type guard: filter invalid values.
        return state.modalStack.filter(Boolean);
    }
    // Fallback: use single ?modal=...
    return readSingleFromUrl(window.location);
}
// Persist a stack into history state and URL. Use push or replace based on navigation intent.
function commitNav(stack, mode) {
    const url = writeUrlForStack(window.location, stack);
    if (mode === "push") {
        window.history.pushState({ modalStack: stack }, "", url);
    }
    else {
        window.history.replaceState({ modalStack: stack }, "", url);
    }
}
export const useModal = (defaultValue, modalId) => {
    const getIsOpenFromNav = useCallback(() => {
        if (IS_SERVER)
            return false;
        const stack = readNavStack();
        return isInStack(stack, modalId);
    }, [modalId]);
    const [isOpen, setIsOpen] = useState(() => getIsOpenFromNav());
    const open = useCallback(() => {
        if (IS_SERVER)
            return;
        // Open puts this modal on top while keeping others in the in-memory stack (history state).
        const current = readNavStack();
        const next = ensureOnTop(current, modalId);
        commitNav(next, "push");
        setIsOpen(true);
    }, [modalId]);
    // Replace the current top with this modal, others remain in the stack.
    // If there are none, simply open this one.
    const openAndReplace = useCallback(() => {
        if (IS_SERVER)
            return;
        const current = readNavStack();
        let next = current.slice();
        if (next.length === 0) {
            next = [modalId];
        }
        else {
            next[next.length - 1] = modalId;
            // Remove any earlier duplicates of modalId.
            next = next.filter((id, idx) => id !== modalId || idx === next.length - 1);
        }
        commitNav(next, "replace");
        setIsOpen(true);
    }, [modalId]);
    // Remove this modal from the stack wherever it is.
    const close = useCallback(() => {
        if (IS_SERVER)
            return;
        const current = readNavStack();
        const next = removeFromStack(current, modalId);
        if (next.length !== current.length) {
            commitNav(next, "replace");
        }
        setIsOpen(false);
    }, [modalId]);
    const toggle = useCallback(() => {
        // Toggle checks navigation stack, not a local cache.
        if (getIsOpenFromNav())
            close();
        else
            open();
    }, [getIsOpenFromNav, close, open]);
    useEffect(() => {
        if (IS_SERVER)
            return;
        // Clean up specific modals from URL/stack on page load.
        // If you do not want a modal to persist on refresh, add it to MODALS_TO_REMOVE_ON_REFRESH.
        const initial = readNavStack();
        const filtered = initial.filter((id) => !MODALS_TO_REMOVE_ON_REFRESH.includes(id));
        if (filtered.length !== initial.length) {
            commitNav(filtered, "replace");
        }
        // On refresh, ensure only a single modal is present in the URL.
        // The full stack remains in history.state so the back button navigates one by one.
        let stackNow = readNavStack();
        // De-duplicate while preserving order and ensure the top is last.
        if (stackNow.length > 1) {
            const unique = [];
            for (const id of stackNow) {
                if (!unique.includes(id))
                    unique.push(id);
            }
            stackNow = unique;
        }
        // Persist the normalized stack and rewrite the URL accordingly (single ?modal=<top>).
        commitNav(stackNow, "replace");
        // Handle "default open" behavior.
        const stack = readNavStack();
        const present = isInStack(stack, modalId);
        // If the URL explicitly names a modal, treat that as the source of truth
        // and suppress default-open for other modals.
        const urlTop = readSingleFromUrl(window.location).at(-1);
        const urlSpecifiesDifferentModal = !!urlTop && urlTop !== modalId;
        if (defaultValue && !present && !urlSpecifiesDifferentModal) {
            const next = ensureOnTop(stack, modalId);
            commitNav(next, "push");
            setIsOpen(true);
        }
        else {
            setIsOpen(present && !urlSpecifiesDifferentModal);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Sync local state with back/forward or external changes.
    // Any navigation (popstate) re-evaluates the history state and updates "isOpen" accordingly.
    useEffect(() => {
        if (IS_SERVER)
            return;
        const onPop = () => {
            // Important: event.state can be null so rely on readNavStack() which handles both.
            setIsOpen(getIsOpenFromNav());
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, [getIsOpenFromNav]);
    return { isOpen, open, close, toggle, openAndReplace };
};
